import React, {ReactNode, FC, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import pick from 'lodash.pick';
import Box from '@material-ui/core/Box';
import {getLocale} from '@epic-core/common/dist';
import {Datapoint} from '@epic-visualizations/charts';
import {useComparisonColor} from '@epic-visualizations/theme';

import {PLAYRATE_HIGHLIGHT_HIGHT} from '../../playrate/PlayrateHighlights';
import useWidth from './useWidth';
import Legends from './legends/Legends';
import {ChartSkeleton} from '../../../../../common/skeleton';
import PlayrateTooltip from './PlayrateTooltip';
import useInteractiveLegends from './legends/useInteractiveLegends';
import {getColor} from './utils';

import {PLAYRATE_DATA_COLORS} from '../../constants';

import {KeysMap, Trends} from './types';
import {Option, Formatter} from '../../../../../../types';

import {ChartContainer, StyledLineChart} from './styles';

export const StyledPlayrateChart = styled(StyledLineChart)`
    & [class*='recharts-tooltip-wrapper'] {
        width: 200px;

        & > div {
            padding: 10px 12px;

            & > p {
                font-size: 12px;
            }
        }
    }
`;

export interface ChartProps {
    data: Datapoint[];
    height?: number;
    xTickFormatter?: Formatter<string | number>;
    yTickFormatter?: Formatter<string | number>;
    tooltipFormatter?: any;
    selectedVersions: Option[];
    xAxis?: string;
    trends?: Trends;
    limit?: number;
    loading: boolean;
    keysMap?: KeysMap;
    emptyDataKeys?: string[];
    yAxisProps?: object;
    xAxisProps?: object;
    children?: ReactNode;
}

const defaultFormatter: Formatter<undefined> = () => undefined;

const PlayrateChart: FC<ChartProps> = ({
    data: propsData,
    height,
    xTickFormatter = defaultFormatter,
    yTickFormatter = defaultFormatter,
    tooltipFormatter = defaultFormatter,
    trends,
    limit = 2,
    loading,
    selectedVersions,
    keysMap = {},
    emptyDataKeys = [],
    children,
    ...props
}: ChartProps) => {
    const [containerRef] = useWidth();
    const getComparisonColor = useComparisonColor();

    const tooltipProps = {
        hideLabel: true,
        isAnimationActive: true,
        allowEscapeViewBox: {x: false, y: false}
    };

    // Used to display legends list
    const dataKeys = useMemo(() => selectedVersions.map(({value}) => String(value)), [
        selectedVersions
    ]);

    const {
        hoveredKey,
        hiddenKeys,
        setHiddenKeys,
        onLegendClick,
        onLegendMouseEnter,
        onLegendMouseLeave
    } = useInteractiveLegends();

    useEffect(() => {
        setHiddenKeys(
            Array.from(new Set([...dataKeys.slice(limit, dataKeys.length), ...emptyDataKeys]))
        );
    }, [dataKeys, emptyDataKeys]);

    // Override the default LineChart colors for hover effect
    const dataColors = useMemo(
        () =>
            dataKeys.reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: getColor(
                        getComparisonColor(PLAYRATE_DATA_COLORS[key]),
                        hoveredKey && !hiddenKeys.includes(hoveredKey) && hoveredKey !== key
                    )
                }),
                {}
            ),
        [getComparisonColor, hoveredKey, dataKeys]
    );

    const data = useMemo(() => {
        const keysToShow = ['percentage', ...dataKeys.filter(key => !hiddenKeys.includes(key))];

        return propsData.map(item => pick(item, keysToShow));
    }, [propsData, hiddenKeys, dataKeys]);

    if (loading) {
        return <ChartSkeleton pb={2} pt={4} height={500} width="100%" hasLegend={false} />;
    }

    const locale = getLocale();

    return (
        <>
            <ChartContainer ref={containerRef}>
                <StyledPlayrateChart
                    responsive
                    style={{padding: 0}}
                    height={height}
                    data={data}
                    locales={locale}
                    showLegend={false}
                    dataColors={dataColors}
                    xTickFormatter={xTickFormatter(locale)}
                    yTickFormatter={yTickFormatter(locale)}
                    margin={{top: 5, right: 15, bottom: PLAYRATE_HIGHLIGHT_HIGHT, left: 10}}
                    tooltipProps={tooltipProps}
                    tooltipContent={PlayrateTooltip({
                        tooltipFormatter: yTickFormatter(locale)
                    })}
                    {...props}
                />
            </ChartContainer>
            <Box mb={2}>
                <Legends
                    isCompact={false}
                    isTooltip={false}
                    keysMap={keysMap}
                    limit={limit}
                    hoveredKey={hoveredKey}
                    hiddenKeys={hiddenKeys}
                    dataKeys={dataKeys}
                    dataColors={PLAYRATE_DATA_COLORS}
                    emptyDataKeys={emptyDataKeys}
                    selectedGames={selectedVersions}
                    onClick={onLegendClick}
                    onMouseEnter={onLegendMouseEnter}
                    onMouseLeave={onLegendMouseLeave}>
                    {children}
                </Legends>
            </Box>
        </>
    );
};

export default PlayrateChart;
